import { useMergeRefs } from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, type ReactNode, useRef } from 'react'
import CheckIcon from '#icons/check-24.svg'
import { fragments, style } from '#styles/index'

const labelCheckboxClass = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  cursor: pointer;


  > .label {
    cursor: pointer;
    display: flex;
    align-items: center;
    color: ${style.themeColors.textDefault};
    ${fragments.textStyle.body.m.regular};
    
    &.disabled {
      color: ${style.themeColors.textDisabled}
    }
  }
`

const checkboxClass = css`
  appearance: none;
  position: relative;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-height: 16px;
  max-width: 16px;

  transition: 0.25s ease outline-color, 0.25s ease border-color, 0.25s ease background-color;
  
  border: 1px solid ${style.themeColors.textDeselected};
  border-radius: none;
  outline-color: transparent;

  cursor: pointer;

  &:hover {
    border-color: ${style.themeColors.lineInputHover};
  }
  &:focus {
    outline: 2px solid ${style.themeColors.lineFocus};
    outline-offset: 0;
    border-color: ${style.themeColors.lineDefault};
  }

  &:before {
    transition: 0.25s ease background-color;
    content: " ";
    display: block;
    position: absolute;
    background-color: transparent;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }


  &:checked { 
    &:before {
      mask:
        url(${CheckIcon}) center/contain no-repeat,
        // color is irrelevant, only required to fill the space for the mask to apply correctly
        linear-gradient(black 0 0);
      mask-composite: exclude;
      background-color: ${style.themeColors.textImportant};
    }


    
    background-color: ${style.themeColors.backgroundDefault};
    border-color: ${style.themeColors.textImportant};

    &:focus {
      outline: 2px solid ${style.themeColors.lineFocus};
      outline-offset: 0;
    }
    &:hover {
      background-color: ${style.themeColors.backgroundDefault};
      border-color: ${style.themeColors.textDanger};
      &:before {
        background-color: ${style.themeColors.textDanger};
      }
    }
  }

  &:disabled {
    border-color: ${style.themeColors.textDisabled};
    background: ${style.themeColors.textDisabled};

    &:before {
      background-color: ${style.themeColors.backgroundBoxed};
    }
    &:hover {
      border-color: ${style.themeColors.textDisabled};
      background: ${style.themeColors.textDisabled};
      &:before {
        background-color: ${style.themeColors.backgroundBoxed};
      }
    }
  }

  &.intermediate, &.intermediate:disabled {
    border-color: ${style.themeColors.lineInput};
    mask-composite: exclude;
    mask-size: 100%;
    background-color: ${style.themeColors.backgroundDefault};
    position: relative;
    &:disabled {
      background-color: ${style.themeColors.backgroundBoxed};
    }
    &:before {
      mask: none;
    }
    &:hover {
      background-color: ${style.themeColors.backgroundDefault};
      border-color: ${style.themeColors.lineInput};
    }

    &:before {
      display: block;
      position: absolute;
      transform: scale(50%);
      content: " ";
      background-color: ${style.themeColors.textImportant};
    }
  }

  &.invalid, &.invalid:disabled {
    border-color: ${style.themeColors.textDanger};
    &:before {
      mask: none;
      background-color: ${style.colors.red[200]};
    }
    &:disabled {
      &:before {
        background-color: ${style.themeColors.backgroundBoxed};
      }
    }
    &:hover {
      border-color: ${style.themeColors.textImportant};
    }
  }
`

export const Checkbox: FC<
  {
    label?: ReactNode
    type?: 'checkbox'
    kind?: 'default' | 'intermediate' | 'invalid'
    clickableLabel?: boolean
  } & Omit<HTMLProps<HTMLInputElement>, 'label'>
> = ({ ref, label, className, kind = 'default', disabled, clickableLabel = true, ...props }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const mergedRefs = useMergeRefs([inputRef, ref])
  if (label) {
    return (
      <div className={cx(labelCheckboxClass, className)}>
        <input
          ref={mergedRefs}
          type="checkbox"
          className={cx(checkboxClass, 'checkbox', kind !== 'default' && kind, className)}
          disabled={disabled}
          {...props}
        />
        <label
          className={cx('label', disabled && 'disabled')}
          onClick={() => {
            inputRef.current?.click()
          }}
        >
          {label}
        </label>
      </div>
    )
  }
  return (
    <input
      ref={ref}
      type="checkbox"
      className={cx(checkboxClass, 'checkbox', kind !== 'default' && kind, className)}
      disabled={disabled}
      {...props}
    />
  )
}
